import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NbToastrService } from '@nebular/theme';
import { Observable } from 'rxjs';
import { ProfileDetailModel } from '../../profile-management/profile-detail.model';
import { OpportunityManagementService } from '../opportunity-management.service';
import { DynamicContentService } from 'src/app/shared/services/dynamic-content.service';
import { DynamicContentModel } from 'src/app/shared/models/dynamic-content.model';
import { ReturnResult } from 'src/app/shared/models/return-result';
import { environment } from 'src/environments/environment';
import { ConvertOppToLeadContactItem, ConvertOppToLeadInputModel } from './convert-opp-to-lead.model';
import { ContactService } from 'src/app/shared/services/contact.service';
import { Page } from 'src/app/shared/models/paging/page';

@Component({
  selector: 'app-convert-opp-to-lead',
  templateUrl: './convert-opp-to-lead.component.html',
  styleUrls: ['./convert-opp-to-lead.component.scss']
})
export class ConvertOppToLeadComponent implements OnInit {
  environment = environment;
  isLoading = false;
  isLoadingContacts = false;
  opportunityModel: ProfileDetailModel;
  contactItems: ConvertOppToLeadContactItem[] = [];
  submitModel: ConvertOppToLeadInputModel = new ConvertOppToLeadInputModel();

  leadInterestOptions = ['Cold Lead', 'Warm Lead', 'Hot Lead'];

  dynamicContentLeadSourceAPI: Observable<ReturnResult<DynamicContentModel[]>>;

  tagsInput: string = '';
  leadSourceCtrl: FormControl = new FormControl('');

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ConvertOppToLeadComponent>,
    private opportunityService: OpportunityManagementService,
    private dynamicContentService: DynamicContentService,
    private contactService: ContactService,
    private toast: NbToastrService,
    private cd: ChangeDetectorRef
  ) {
    this.opportunityModel = data.model;
  }

  ngOnInit() {
    this.dynamicContentLeadSourceAPI = this.dynamicContentService.getDynamicContentByType('opportunity-leadsource');

    this.submitModel.opportunityId = this.opportunityModel.profileId;
    this.submitModel.opportunityName = this.opportunityModel?.saleOpportunity?.opportunityName || this.opportunityModel?.displayName || '';

    this.loadContacts();
  }

  loadContacts() {
    this.isLoadingContacts = true;
    const profileId = this.opportunityModel?.profileId;
    if (!profileId) {
      this.fallbackContacts();
      this.isLoadingContacts = false;
      this.cd.detectChanges();
      return;
    }
    const page = new Page();
    page.size = -1;
    page.pageNumber = 0;
    this.contactService.getContactRelationShip(profileId, 'Profile', page).subscribe({
      next: (res) => {
        const rawContacts: any[] = res?.result?.data || [];
        this.contactItems = rawContacts.map((c) => {
          const contactData = c?.contact || c;
          return new ConvertOppToLeadContactItem(contactData);
        });
        this.fallbackContacts();
        this.isLoadingContacts = false;
        this.cd.detectChanges();
      },
      error: () => {
        this.fallbackContacts();
        this.isLoadingContacts = false;
        this.cd.detectChanges();
      }
    });
  }

  private fallbackContacts() {
    if (this.contactItems.length === 0) {
      const profileContacts = this.opportunityModel?.profileContacts || [];
      this.contactItems = profileContacts.map((pc: any) => new ConvertOppToLeadContactItem(pc.contact || pc));
      if (this.contactItems.length === 0 && this.opportunityModel?.contact) {
        this.contactItems.push(new ConvertOppToLeadContactItem(this.opportunityModel.contact));
      }
    }
  }

  getLeadInterestColor(interest: string): string {
    switch (interest) {
      case 'Hot Lead': return '#ff4d4f';
      case 'Warm Lead': return '#faad14';
      case 'Cold Lead':
      default: return '#1890ff';
    }
  }

  toggleContact(item: ConvertOppToLeadContactItem) {
    item.selected = !item.selected;
  }

  toggleAll(checked: boolean) {
    this.contactItems.forEach(item => item.selected = checked);
  }

  get isAllSelected(): boolean {
    return this.contactItems.length > 0 && this.contactItems.every(item => item.selected);
  }

  get selectedContacts(): ConvertOppToLeadContactItem[] {
    return this.contactItems.filter(item => item.selected);
  }

  onTagsChange() {
    this.submitModel.tags = this.tagsInput;
  }

  onSubmitClicked() {
    if (this.selectedContacts.length === 0) {
      this.toast.warning('Please select at least one contact', 'Warning');
      return;
    }

    this.isLoading = true;
    this.submitModel.contacts = this.selectedContacts;
    this.submitModel.leadSource = this.leadSourceCtrl.value || '';

    this.opportunityService.convertToLeads(this.submitModel).subscribe({
      next: (data) => {
        if (data.result) {
          this.toast.success(`Converted ${this.selectedContacts.length} contact(s) to Lead`, 'Success');
          this.isLoading = false;
          this.dialogRef.close(true);
        } else {
          this.toast.danger(data.message || 'Convert failed', 'Error');
          this.isLoading = false;
        }
      },
      error: (err) => {
        this.toast.danger('Convert Error', 'Error');
        this.isLoading = false;
      }
    });
  }
}
